const GRADIENT_TOP = 'bg-gradient-top';
const GRADIENT_MIDDLE = 'bg-gradient-middle';
const GRADIENT_BOTTOM = 'bg-gradient-bottom';

// The button always keeps the bottom-left corner, so it reads as one family with the control bar
// of the video slider, which is anchored to the same left edge. The hero frame pads its content
// by `p-5 sm:p-8`, and the same inset lines the button up with the text block.
const BUTTON_BOTTOM_LEFT = 'bottom-5 left-5 sm:bottom-8 sm:left-8';

export interface HeroContentPosition {
  /** Flex alignment plus text alignment of the content block inside the hero. */
  contentClasses: string;
  /** Horizontal alignment of the cta, which is a `w-fit` block and does not inherit it. */
  buttonClasses?: string;
  /** The bottom-left corner the pause/play button of the background video sits in. */
  playbackButtonClasses: string;
  gradient: string;
}

export interface HeroOverlayClasses {
  /** Absent when the editor switched the overlay off, so no overlay layer is rendered. */
  gradientClass?: string;
  textClass: string;
}

/**
 * The nine `content-position` selections of the shape, mirroring the switch in the
 * ImageWithContent tile (`src/ui/components/tiles/ImageWithContent`). A lookup keeps every
 * combination in one readable place and lets an unknown or unset selection fall back without
 * extra branching.
 */
const CONTENT_POSITIONS: Record<string, HeroContentPosition | undefined> = {
  'center-center': {
    contentClasses: 'justify-center items-center text-center',
    buttonClasses: 'mx-auto',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_MIDDLE,
  },
  'center-top': {
    contentClasses: 'justify-center items-start text-center',
    buttonClasses: 'mx-auto',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_TOP,
  },
  'center-bottom': {
    contentClasses: 'justify-center items-end text-center',
    buttonClasses: 'mx-auto',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_BOTTOM,
  },
  'left-top': {
    contentClasses: 'justify-start items-start',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_TOP,
  },
  'left-center': {
    contentClasses: 'justify-start items-center',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_MIDDLE,
  },
  'left-bottom': {
    contentClasses: 'justify-start items-end',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_BOTTOM,
  },
  'right-top': {
    contentClasses: 'justify-end items-start text-right',
    buttonClasses: 'ml-auto',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_TOP,
  },
  'right-center': {
    contentClasses: 'justify-end items-center text-right',
    buttonClasses: 'ml-auto',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_MIDDLE,
  },
  'right-bottom': {
    contentClasses: 'justify-end items-end text-right',
    buttonClasses: 'ml-auto',
    playbackButtonClasses: BUTTON_BOTTOM_LEFT,
    gradient: GRADIENT_BOTTOM,
  },
};

const DEFAULT_CONTENT_POSITION: HeroContentPosition = {
  contentClasses: 'justify-center items-center',
  buttonClasses: 'mx-auto',
  playbackButtonClasses: BUTTON_BOTTOM_LEFT,
  gradient: GRADIENT_MIDDLE,
};

/** The light gradients mirror the direction of their dark counterpart from the tailwind config. */
const LIGHT_GRADIENTS: Record<string, string | undefined> = {
  [GRADIENT_TOP]: 'bg-gradient-to-b from-white/70 to-transparent',
  [GRADIENT_MIDDLE]: 'bg-white/40',
  [GRADIENT_BOTTOM]: 'bg-gradient-to-t from-white/70 to-transparent',
};

export function getContentPosition(value: string | undefined): HeroContentPosition {
  return CONTENT_POSITIONS[value ?? ''] ?? DEFAULT_CONTENT_POSITION;
}

/**
 * Translates the `overlay-style` selection into the overlay layer and the text colour that
 * stays readable on top of it.
 */
export function getOverlayClasses(
  overlayStyle: string | undefined,
  gradient: string,
): HeroOverlayClasses {
  if (overlayStyle === 'none') {
    return { textClass: 'text-white' };
  }

  if (overlayStyle === 'light') {
    return { gradientClass: LIGHT_GRADIENTS[gradient], textClass: 'text-neutral-100' };
  }

  return { gradientClass: gradient, textClass: 'text-white' };
}
